import { useState, useEffect } from "react";
import { Link, router } from "@inertiajs/react";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { Button, buttonVariants } from "@/components/ui/button";
import { MessageSquareText } from "lucide-react";

import { LockedButton } from "@/components/billing/LockedButton";
import { UpgradeModal } from "@/components/billing/UpgradeModal";
import type { PageProps } from "@/types";
import { whatsappConnectionUi } from "./whatsappConnection";

interface Business {
  id: number;
  name: string;
  slug: string;
  timezone: string;
  currency: string;
}

interface SettingsIndexProps extends PageProps {
  business: Business;

  kapso_whatsapp?: {
    status: string;
    display_address: string | null;
    agent_enabled: boolean;
    reminders_enabled: boolean;
  } | null;
  can_manage_billing?: boolean;
}

type WhatsappFeature = "agent_enabled" | "reminders_enabled";

function SettingsSwitch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-11 w-14 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-primary" : "bg-muted"
      }`}
    >
      <span
        className={`block h-6 w-6 rounded-full bg-background shadow-sm transition-transform ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function AgentPreviewAction({ orgSlug }: { orgSlug: string }) {
  return (
    <div className="mt-4 flex flex-col gap-3 pl-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-medium text-foreground">Prueba tu agente</p>
        <p className="text-sm text-muted-foreground">
          Conversa como cliente usando tus servicios y disponibilidad reales.
        </p>
      </div>
      <Link
        href={`/${orgSlug}/chatbot_preview`}
        className={buttonVariants({ variant: "outline" })}
      >
        <MessageSquareText className="h-4 w-4" />
        Probar agente
      </Link>
    </div>
  );
}

interface GoogleCalendarAccount {
  email: string;
  name: string | null;
  status: string;
  last_error: string | null;
}

interface GoogleCalendarSync {
  id: number;
  google_calendar_id: string;
  summary: string;
  status: string;
  last_error: string | null;
  last_webhook_received_at: string | null;
  last_incremental_sync_at: string | null;
}

interface GoogleCalendarEntry {
  id: string;
  summary: string;
  access_role: string;
}

interface GoogleCalendarSettings {
  account: GoogleCalendarAccount | null;
  sync: GoogleCalendarSync | null;
  calendars: GoogleCalendarEntry[];
}

export default function SettingsIndex({
  business,

  kapso_whatsapp,
  can_manage_billing,
  billing,
  current_user,
  current_organization,
  current_business,
  flash,
}: SettingsIndexProps) {
  const orgSlug = current_organization?.slug || "";

  const [connectingWhatsApp, setConnectingWhatsApp] = useState(false);
  const [disconnectingWhatsApp, setDisconnectingWhatsApp] = useState(false);
  const [agentEnabled, setAgentEnabled] = useState(kapso_whatsapp?.agent_enabled ?? true);
  const [remindersEnabled, setRemindersEnabled] = useState(kapso_whatsapp?.reminders_enabled ?? true);
  const [savingWhatsAppFeature, setSavingWhatsAppFeature] = useState<WhatsappFeature | null>(null);
  const [whatsAppFeatureError, setWhatsAppFeatureError] = useState<string | null>(null);
  const [googleCalendar, setGoogleCalendar] = useState<GoogleCalendarSettings | null>(null);
  const [selectedGoogleCalendarId, setSelectedGoogleCalendarId] = useState("");
  const [googleCalendarError, setGoogleCalendarError] = useState<string | null>(null);
  const [savingGoogleCalendar, setSavingGoogleCalendar] = useState(false);
  const [disconnectingGoogleCalendar, setDisconnectingGoogleCalendar] = useState(false);
  const [billingAction, setBillingAction] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") === "upgrade") {
      setShowUpgradeModal(true);
    }
    if (params.get("billing") === "success" && billing?.status === "trialing") {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (attempts > 5) {
          clearInterval(interval);
          return;
        }
        router.reload({ only: ["billing"] });
      }, 2000);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    fetch(`/${orgSlug}/settings/google_calendar`, { headers: { Accept: "application/json" } })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("No pudimos cargar Google Calendar.")))
      .then((data: GoogleCalendarSettings | null) => {
        if (!data) return;
        setGoogleCalendarError(null);
        setGoogleCalendar(data);
        setSelectedGoogleCalendarId(data.sync?.google_calendar_id || data.calendars[0]?.id || "");
      })
      .catch((error) => {
        setGoogleCalendarError(error.message);
        setGoogleCalendar({ account: null, sync: null, calendars: [] });
      });
  }, [orgSlug]);

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("es-MX", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

  const handleWhatsAppFeatureChange = async (feature: WhatsappFeature, enabled: boolean) => {
    const previous = feature === "agent_enabled" ? agentEnabled : remindersEnabled;
    const setValue = feature === "agent_enabled" ? setAgentEnabled : setRemindersEnabled;
    setValue(enabled);
    setSavingWhatsAppFeature(feature);
    setWhatsAppFeatureError(null);

    try {
      const response = await fetch(`/${orgSlug}/integrations/kapso/whatsapp`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ [feature]: enabled }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.error || "No pudimos guardar la configuración.");

      setAgentEnabled(data.kapso_whatsapp.agent_enabled);
      setRemindersEnabled(data.kapso_whatsapp.reminders_enabled);
    } catch (error) {
      setValue(previous);
      setWhatsAppFeatureError(
        error instanceof Error ? error.message : "No pudimos guardar la configuración.",
      );
    } finally {
      setSavingWhatsAppFeature(null);
    }
  };

  const handleDisconnectWhatsApp = () => {
    if (!confirm("¿Estás seguro de desconectar tu WhatsApp Business? El agente de IA dejará de responder mensajes.")) return;
    setDisconnectingWhatsApp(true);
    router.delete(`/${orgSlug}/integrations/kapso/whatsapp`, {
      onFinish: () => setDisconnectingWhatsApp(false),
    });
  };

  const handleConnectWhatsApp = () => {
    setConnectingWhatsApp(true);
    router.post(
      `/${orgSlug}/integrations/kapso/whatsapp/setup_link`,
      undefined,
      {
        onFinish: () => setConnectingWhatsApp(false),
      },
    );
  };

  const handleConnectGoogleCalendar = () => {
    window.location.href = `/google_calendar/oauth/start`;
  };

  const handleSaveGoogleCalendar = async () => {
    if (!selectedGoogleCalendarId) return;
    setSavingGoogleCalendar(true);
    setGoogleCalendarError(null);
    try {
      const response = await fetch(`/${orgSlug}/settings/google_calendar/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ google_calendar_id: selectedGoogleCalendarId }),
      });
      if (response.ok) {
        const data = await response.json();
        setGoogleCalendar((prev) => prev ? { ...prev, sync: data.sync } : prev);
      } else {
        const data = await response.json().catch(() => null);
        setGoogleCalendarError(data?.error || "No pudimos guardar el calendario.");
      }
    } catch {
      setGoogleCalendarError("No pudimos guardar el calendario.");
    } finally {
      setSavingGoogleCalendar(false);
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    if (!confirm("¿Desconectar Google Calendar? Tippy dejará de sincronizar citas y bloqueos.")) return;
    setDisconnectingGoogleCalendar(true);
    setGoogleCalendarError(null);
    try {
      const response = await fetch(`/${orgSlug}/settings/google_calendar/disconnect`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken() },
      });
      if (response.ok) {
        setGoogleCalendar({ account: null, sync: null, calendars: [] });
        setSelectedGoogleCalendarId("");
      } else {
        setGoogleCalendarError("No pudimos desconectar Google Calendar.");
      }
    } catch {
      setGoogleCalendarError("No pudimos desconectar Google Calendar.");
    } finally {
      setDisconnectingGoogleCalendar(false);
    }
  };

  const handlePortal = () => {
    setBillingAction("portal");
    router.post(`/${orgSlug}/billing/portal`, undefined, {
      onFinish: () => setBillingAction(null),
    });
  };

  const whatsappUi = whatsappConnectionUi(kapso_whatsapp, billing?.plan);

  const planLabel = (plan?: string | null) => {
    switch (plan) {
      case "basic":
        return "Básico";
      case "pro":
        return "Pro";
      default:
        return "Always Free";
    }
  };

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Configuración
          </h1>
          <p className="text-muted-foreground">
            Configura los horarios de tu negocio
          </p>
        </div>

        {flash?.notice && (
          <div className="rounded-lg bg-green-500/10 p-3 text-sm text-green-500">
            {flash.notice}
          </div>
        )}

        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border p-4">
            <h2 className="font-medium">WhatsApp</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Administra tu conexión y las funciones de WhatsApp.
            </p>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <div className="text-sm">
                Estado:{" "}
                <span className={whatsappUi.statusClass}>{whatsappUi.statusLabel}</span>
              </div>
              {kapso_whatsapp?.display_address && (
                <div className="mt-1 text-sm text-muted-foreground">
                  Número: {kapso_whatsapp.display_address}
                </div>
              )}
            </div>

            {whatsappUi.showUpgradeLock ? (
              <LockedButton
                isLocked={true}
                feature="whatsapp"
                orgSlug={orgSlug}
              >
                Mejorar plan
              </LockedButton>
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={handleConnectWhatsApp}
                  disabled={connectingWhatsApp}
                >
                  {connectingWhatsApp
                    ? "Abriendo Kapso…"
                    : whatsappUi.primaryAction === "reconnect"
                      ? "Reconectar"
                      : "Conectar"}
                </Button>
                {whatsappUi.canDisconnect && (
                  <Button
                    variant="outline"
                    onClick={handleDisconnectWhatsApp}
                    disabled={disconnectingWhatsApp}
                    className="text-destructive border-destructive hover:bg-destructive/10"
                  >
                    {disconnectingWhatsApp ? "Desconectando…" : "Desconectar"}
                  </Button>
                )}
              </div>
            )}

            {whatsappUi.showFeatureToggles ? (
              <div className="border-t border-border pt-4">
                {whatsAppFeatureError && (
                  <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                    {whatsAppFeatureError}
                  </div>
                )}

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-foreground">Agente de IA</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Responde mensajes y ayuda a agendar citas automáticamente.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <SettingsSwitch
                      checked={agentEnabled}
                      disabled={savingWhatsAppFeature !== null}
                      label="Agente de IA"
                      onChange={(checked) => handleWhatsAppFeatureChange("agent_enabled", checked)}
                    />
                    <span className="w-14 text-sm text-foreground">
                      {savingWhatsAppFeature === "agent_enabled"
                        ? "Guardando…"
                        : agentEnabled ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                </div>

                <AgentPreviewAction orgSlug={orgSlug} />

                <div className="mt-4 flex items-start justify-between gap-4 border-t border-border pt-4">
                  <div>
                    <p className="font-medium text-foreground">Recordatorios automáticos</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Envía recordatorios por WhatsApp 24 horas y 1 hora antes de cada cita.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <SettingsSwitch
                      checked={remindersEnabled}
                      disabled={savingWhatsAppFeature !== null}
                      label="Recordatorios automáticos"
                      onChange={(checked) => handleWhatsAppFeatureChange("reminders_enabled", checked)}
                    />
                    <span className="w-14 text-sm text-foreground">
                      {savingWhatsAppFeature === "reminders_enabled"
                        ? "Guardando…"
                        : remindersEnabled ? "Activos" : "Inactivos"}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border-t border-border pt-4">
                <AgentPreviewAction orgSlug={orgSlug} />
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border p-4">
            <h2 className="font-medium">Google Calendar</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Sincroniza tus citas y bloquea horarios ocupados desde tu calendario personal.
              {' '}Consulta cómo usamos los datos de Google Calendar en nuestra{' '}
              <a href="/privacy" className="text-primary hover:underline">Política de Privacidad</a>.
            </p>
          </div>

          <div className="p-4 space-y-4">
            {googleCalendarError && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {googleCalendarError}
              </div>
            )}

            {!googleCalendar ? (
              <div className="text-sm text-muted-foreground">Cargando estado…</div>
            ) : !googleCalendar.account ? (
              <div className="space-y-3">
                <div className="text-sm">
                  Estado: <span className="text-muted-foreground">no conectado</span>
                </div>
                <Button onClick={handleConnectGoogleCalendar}>Conectar Google Calendar</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1 text-sm">
                  <div>
                    Cuenta: <span className="text-foreground">{googleCalendar.account.email}</span>
                  </div>
                  <div>
                    Estado:{" "}
                    <span className={googleCalendar.sync?.status === "active" ? "text-green-600" : googleCalendar.account.status === "revoked" ? "text-yellow-600" : "text-muted-foreground"}>
                      {googleCalendar.account.status === "revoked" ? "necesita reconectar" : (googleCalendar.sync?.status || googleCalendar.account.status)}
                    </span>
                  </div>
                  {googleCalendar.account.last_error && (
                    <div className="text-destructive">Error: {googleCalendar.account.last_error}</div>
                  )}
                  {googleCalendar.sync?.last_error && (
                    <div className="text-destructive">Error de sync: {googleCalendar.sync.last_error}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="google-calendar-select">
                    Calendario
                  </label>
                  {googleCalendar.calendars.length === 0 ? (
                    <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                      No encontramos calendarios editables en esta cuenta. Reconecta Google Calendar o revisa tus permisos.
                    </div>
                  ) : (
                    <select
                      id="google-calendar-select"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={selectedGoogleCalendarId}
                      onChange={(event) => setSelectedGoogleCalendarId(event.target.value)}
                    >
                      {googleCalendar.calendars.map((calendar) => (
                        <option key={calendar.id} value={calendar.id}>
                          {calendar.summary}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
                  <div>Última sync: {formatDateTime(googleCalendar.sync?.last_incremental_sync_at)}</div>
                  <div>Último webhook: {formatDateTime(googleCalendar.sync?.last_webhook_received_at)}</div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleSaveGoogleCalendar}
                    disabled={savingGoogleCalendar || !selectedGoogleCalendarId || googleCalendar.calendars.length === 0 || googleCalendar.account.status === "revoked"}
                  >
                    {savingGoogleCalendar ? "Guardando…" : "Guardar calendario"}
                  </Button>
                  <Button variant="outline" onClick={handleConnectGoogleCalendar}>
                    {googleCalendar.account.status === "revoked" ? "Reconectar Google Calendar" : "Reconectar"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDisconnectGoogleCalendar}
                    disabled={disconnectingGoogleCalendar}
                    className="text-destructive border-destructive hover:bg-destructive/10"
                  >
                    {disconnectingGoogleCalendar ? "Desconectando…" : "Desconectar"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {can_manage_billing && billing && (
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border p-4">
              <h2 className="font-medium">Plan y facturación</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Administra tu plan y el uso de conversaciones.
              </p>
            </div>

            <div className="space-y-4 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">
                    Plan actual
                  </div>
                  <div className="text-lg font-semibold text-foreground">
                    {planLabel(billing.plan)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Estado: {billing.status}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Periodo:{" "}
                  <span className="text-foreground">
                    {formatDate(billing.period_start)} -{" "}
                    {formatDate(billing.period_end)}
                  </span>
                </div>
              </div>

              {billing.usage && billing.usage.total_available > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Conversaciones
                    </span>
                    <span className="text-foreground">
                      {billing.usage.conversations_used} /{" "}
                      {billing.usage.total_available}
                      {billing.usage.pack_credits_remaining > 0 && (
                        <span className="text-muted-foreground">
                          {" "}({billing.usage.conversations_included} incluidas + {billing.usage.pack_credits_remaining} pack)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-primary"
                      style={{
                        width: `${Math.min(100, billing.usage.percentage)}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    “Conversación” = ventana de 24h de WhatsApp.
                  </p>
                </div>
              )}

              {billing.status === "trialing" && billing.trial_days_remaining != null && (
                <div className="rounded-lg bg-blue-500/10 p-3 text-sm text-blue-600">
                  Estás en tu prueba gratis del plan Básico.{" "}
                  <span className="font-semibold">
                    {billing.trial_days_remaining === 0
                      ? "Tu prueba termina hoy."
                      : billing.trial_days_remaining === 1
                        ? "Te queda 1 día."
                        : `Te quedan ${billing.trial_days_remaining} días.`}
                  </span>{" "}
                  Suscríbete para no perder acceso. Tu WhatsApp será desconectado al finalizar la prueba.
                </div>
              )}

              {billing.cancel_at_period_end && billing.period_end && (
                <div className="rounded-lg bg-yellow-500/10 p-3 text-sm text-yellow-600">
                  Tu plan se cancelará el{" "}
                  <span className="font-semibold">{formatDate(billing.period_end)}</span>.
                  Tu WhatsApp será desconectado al finalizar el periodo.
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                {billing.plan === "free" ? (
                  <Button
                    className="w-full"
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    Mejorar plan
                  </Button>
                ) : billing.status === "trialing" ? (
                  <Button
                    className="w-full"
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    Suscribirse al plan Básico
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    onClick={handlePortal}
                    disabled={billingAction !== null}
                  >
                    {billingAction === "portal"
                      ? "Abriendo portal..."
                      : "Administrar suscripción"}
                  </Button>
                )}
              </div>

              {billing.plan !== "free" && (
                <p className="text-xs text-muted-foreground">
                  Cambios de plan y método de pago se gestionan desde el portal.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        orgSlug={orgSlug}
      />
    </DashboardLayout>
  );
}
